'use client';

import {
  IconCheck,
  IconClock,
  IconCopy,
  IconInfoCircle,
  IconRefresh,
} from '@tabler/icons-react';

import { Button, Modal, VStack } from '@j-s/components';

import { CLIENT_STATUS_MAP } from '../constants/messageStatus';
import { useTicketTracker } from '../hooks/useTicketTracker';

import { ClientPushPromptCard } from './ClientPushPromptCard';
import { MessageModalHeader } from './MessageModalHeader';
import { TicketReplyForm } from './TicketReplyForm';
import { TicketReplyList } from './TicketReplyList';

export interface TicketTrackerModalProps {
  ticketId: string | null;
  accessToken: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export const TicketTrackerModal = ({
  ticketId,
  isOpen,
  onClose,
}: TicketTrackerModalProps) => {
  const {
    ticket,
    copied,
    handleSendReply,
    handleCloseTicket,
    handleReopenTicket,
    handleCopyLink,
  } = useTicketTracker(ticketId, isOpen);

  if (!ticket && isOpen) {
    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => !open && onClose()}
        size="md"
        body={
          <div className="py-12 text-center text-gray-500 text-sm">
            Wczytywanie wiadomości...
          </div>
        }
      />
    );
  }

  if (!ticket) return null;

  const statusInfo =
    CLIENT_STATUS_MAP[ticket.status] || CLIENT_STATUS_MAP.unread;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      title={
        <MessageModalHeader
          ticketNumber={ticket.ticketNumber}
          date={ticket.createdAt}
          subject={ticket.subject}
          statusLabel={statusInfo.label}
          statusColor={statusInfo.color}
        />
      }
      body={
        <VStack gap={4} fullWidth>
          <div className="flex items-center justify-between bg-gray-50 px-4 py-2.5 rounded-xl border border-gray-200 text-xs text-gray-600">
            <div className="flex items-center gap-1.5">
              <IconClock size={15} className="text-gray-400" />
              <span>
                Utworzono:{' '}
                {new Date(ticket.createdAt).toLocaleString('pl-PL', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </span>
            </div>
            <button
              type="button"
              onClick={handleCopyLink}
              className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-semibold cursor-pointer"
            >
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              <span>{copied ? 'Skopiowano link!' : 'Kopiuj link'}</span>
            </button>
          </div>

          <TicketReplyList ticket={ticket} />

          {(ticket.status === 'client_read' || ticket.status === 'replied') && (
            <div className="p-3.5 bg-amber-50/80 border border-amber-200/90 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-amber-950 shadow-2xs w-full">
              <div className="flex items-start gap-2.5">
                <IconInfoCircle
                  size={18}
                  className="text-amber-600 shrink-0 mt-0.5"
                />
                <div className="space-y-0.5">
                  <div className="font-bold text-amber-900">
                    Co chcesz teraz zrobić?
                  </div>
                  <div className="text-amber-800/90 leading-snug">
                    Zapoznaj się z odpowiedzią serwisu. Możesz{' '}
                    <strong>odpowiedzieć w wątku poniżej</strong> lub{' '}
                    <strong>zakończyć sprawę</strong>, jeśli wszystko zostało
                    wyjaśnione.
                  </div>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onPress={handleCloseTicket}
                startContent={<IconCheck size={14} />}
                className="shrink-0 self-end sm:self-center"
              >
                Zakończ sprawę
              </Button>
            </div>
          )}

          <TicketReplyForm
            ticket={ticket}
            onSendReply={handleSendReply}
            onReopen={handleReopenTicket}
          />

          {(!ticket.pushTokens || ticket.pushTokens.length === 0) && (
            <ClientPushPromptCard
              ticketId={ticket.id}
              ticketNumber={ticket.ticketNumber}
            />
          )}
        </VStack>
      }
      footer={
        <div className="flex items-center justify-between gap-2 w-full">
          {ticket.status !== 'closed' ? (
            <Button
              variant="secondary"
              size="sm"
              onPress={handleCloseTicket}
              startContent={<IconCheck size={16} />}
            >
              Zakończ sprawę
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onPress={handleReopenTicket}
              startContent={<IconRefresh size={14} />}
            >
              Wznów sprawę
            </Button>
          )}

          <Button variant="primary" size="sm" onPress={onClose}>
            Zamknij
          </Button>
        </div>
      }
    />
  );
};
